import { useSnapshot } from 'valtio';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { getOverlay } from '../components/overlays';
import { ProjectLogin } from '../components/overlays/gmap_overlay';
import Header from '../components/header';
import Loading from '../components/loading';
import DateLabel from '../components/date_label';
import Footer from '../components/footer';
import LatestEvent from '../components/latest_event';
import EventDropdowns from '../components/event_dropdowns';
import SideMenu from '../components/side_menu';
import MobileDetailOverlay from '../components/mobile_detail_overlay';
import Modal from '../components/modal';
import VideoControls from '../components/video_controls';
import DataReadout from '../components/data_readout';
import BackButton from '../components/back_button';
import RectangleSelectButton from '../components/rectangle_select_button'
import CancelRectangleButton from '../components/cancel_rectangle_button';
import ExportButton from '../components/export_button';
import ColorBar from '../components/color_bar';
import { layersStore, stellarStore } from '../managers/globalState';
import KioskMode from '../layouts/KioskLayout';
import AnimationDate from '../components/animation_date';
import AnimationControlsWrapper from '../components/animation_controls_wrapper';
import earthquakelegend from '../../www/assets/images/earthquakelegend.png';
import { eventsStore, videoStore, datasetStore, kioskStore, appStore, uiStore } from '../managers/globalState';

const isIphone = navigator.userAgent.match(/iPhone/i);
const PROJECT_AUTH_EXPIRY_KEY = 'ce2s2-projects-auth-expires-at';
const PROJECT_AUTH_DURATION_MS = 5 * 60 * 1000;

const getProjectAuthentication = () => {
	try {
		const expiresAt = Number(window.localStorage.getItem(PROJECT_AUTH_EXPIRY_KEY));
		const isAuthenticated = expiresAt > Date.now();

		if (!isAuthenticated) {
			window.localStorage.removeItem(PROJECT_AUTH_EXPIRY_KEY);
		}

		return isAuthenticated;
	} catch (error) {
		return false;
	}
};

const setProjectAuthentication = isAuthenticated => {
	try {
		if (isAuthenticated) {
			window.localStorage.setItem(PROJECT_AUTH_EXPIRY_KEY, String(Date.now() + PROJECT_AUTH_DURATION_MS));
		} else {
			window.localStorage.removeItem(PROJECT_AUTH_EXPIRY_KEY);
		}
	} catch (error) {
		// Continue with in-memory authentication when browser storage is unavailable.
	}
};

const MobileComponents = () => (
	<>
		<MobileDetailOverlay />
		<SideMenu />
		<EventDropdowns />
	</>
);

const HideableComponents = () => {
	const { isMobile } = useSnapshot(appStore.state);
	const { currentStellar } = useSnapshot(stellarStore.state);
	const { eventsVisibleOnGlobe, animatedDate } = useSnapshot(eventsStore.state);
	const { earthquakes } = useSnapshot(layersStore.state);
	const showEventDropdowns = !isMobile && eventsVisibleOnGlobe;
  const { currentDataset } = useSnapshot(datasetStore.state);
  const canSelectData = currentDataset && currentDataset.urlParam != "nisar-radar";
	return (


		<>
			<Header />
			{currentStellar ? null : <LatestEvent />}
			{isMobile && <MobileComponents />}
			{earthquakes ? (
				<img
          className="earthquake legend"
					src={earthquakelegend}
					alt="earthquakes legend"
				/>) : null
			}
			<AnimationControlsWrapper />
      { canSelectData && !earthquakes ? 
        (
          createPortal(<div className="rectangle-button-container">
            <RectangleSelectButton /> <ExportButton /> <CancelRectangleButton /> 
          </div>, document.body)
        ) : null
      }
			
			<BackButton />
			<ColorBar />
			{showEventDropdowns && <EventDropdowns />}
			<Footer />
		</>
	);
};

/**
 * This component is dedicated to organizing the UI into it's various sections.
 * To reduce and simplify the code, functional components and react hooks:
 * https://reactjs.org/docs/hooks-reference.html
 */
export default function UILayout() {
	const [isProjectAuthenticated, setIsProjectAuthenticated] = useState(getProjectAuthentication);
	const { isVideoTextureLoaded } = useSnapshot(videoStore.state);
	const { datasetHasAnimation } = useSnapshot(datasetStore.state);
	const { maxSessionTime, maxInactivityTime } = useSnapshot(kioskStore.state);
	const { queries, isMobile } = useSnapshot(appStore.state);
	const { hideUI } = queries || {};
	const showUI = hideUI !== 'true';

	// overlayType state.
	const { overlayType } = useSnapshot(uiStore.state);
	const signOutOfProjects = () => {
		setProjectAuthentication(false);
		setIsProjectAuthenticated(false);
	};
	const closeOverlay = () => {
		uiStore.setGlobalState({ overlayType: null, videoMobileBackTo: null });
	};
	const authenticateProjects = () => {
		setProjectAuthentication(true);
		setIsProjectAuthenticated(true);
	};

	useEffect(() => {
		if (overlayType === 'gmap') {
			setIsProjectAuthenticated(getProjectAuthentication());
		}
	}, [overlayType]);

	const classes = `ui${datasetHasAnimation ? ' animating' : ''}${isIphone ? ' move-up' : ''}${isVideoTextureLoaded ? ' video-animating' : ''}${showUI ? '' : ' hide-ui'}`;

	return (
		<>
			<div id='UI' className={classes}>
				<React.StrictMode>
					{/* Hideable components */}
					{showUI && <HideableComponents />}

					{/* Components to always show */}
					<VideoControls />
					<Loading />
					<DataReadout />
					<Modal />
					{!isMobile && <DateLabel />}

					{overlayType === 'gmap' && !isProjectAuthenticated
						? <ProjectLogin onCancel={closeOverlay} onSuccess={authenticateProjects} />
						: null}
					{overlayType && (overlayType !== 'gmap' || isProjectAuthenticated)
						? getOverlay(overlayType, { closeOverlay, onProjectSignOut: signOutOfProjects })
						: null}
				</React.StrictMode>
			</div>
			{Boolean(maxSessionTime || maxInactivityTime) && <KioskMode />}
		</>
	);
}
